import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  Subscription,
  debounceTime,
  filter,
  fromEvent,
  interval,
  merge,
  take,
} from 'rxjs';
import {
  AppVersionUpdateDialog,
  AppVersionUpdateDialogResult,
} from './app-version-update-dialog';

export const APP_VERSION_PATH = '/assets/version.json';
export const APP_VERSION_UPDATE_DIALOG_ID = 'app-version-update-dialog';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const EVENT_DEBOUNCE_MS = 750;
const REQUEST_TIMEOUT_MS = 8000;
const MAX_VERSION_LENGTH = 200;

export function isAppVersionRequestUrl(url: string): boolean {
  return url.includes(APP_VERSION_PATH);
}

export function parseAppVersionPayload(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const raw = (payload as { version?: unknown }).version;
  if (typeof raw !== 'string') {
    return null;
  }
  const version = raw.trim();
  if (!version || version.length > MAX_VERSION_LENGTH || version.startsWith('<')) {
    return null;
  }
  return version;
}

@Injectable({ providedIn: 'root' })
export class AppVersionCheckService {
  private readonly dialog = inject(MatDialog);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private currentVersion: string | null = null;
  private detectedServerVersion: string | null = null;
  private dismissedVersion: string | null = null;
  private promptedVersion: string | null = null;
  private updatePending = false;
  private updatePopupOpen = false;
  private requestInFlight = false;
  private lastCheckAt = 0;
  private started = false;
  private reloadArmed = false;
  private pendingCloseSub: Subscription | null = null;
  private updateDialogRef: MatDialogRef<
    AppVersionUpdateDialog,
    AppVersionUpdateDialogResult
  > | null = null;

  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) {
      return;
    }
    this.started = true;
    this.destroyRef.onDestroy(() => {
      this.clearPendingListener();
      this.updateDialogRef = null;
    });

    try {
      this.bindCheckPipeline();
      void this.runCheck();
    } catch {
      this.started = false;
    }
  }

  private bindCheckPipeline(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    );
    const focus$ = fromEvent(window, 'focus');

    merge(interval(CHECK_INTERVAL_MS), visibility$, focus$)
      .pipe(
        debounceTime(EVENT_DEBOUNCE_MS),
        filter(() => this.shouldCheck()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        void this.runCheck();
      });
  }

  private shouldCheck(): boolean {
    if (this.requestInFlight) {
      return false;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return false;
    }
    return this.lastCheckAt === 0 || Date.now() - this.lastCheckAt >= CHECK_INTERVAL_MS;
  }

  private async runCheck(): Promise<void> {
    if (this.requestInFlight) {
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }

    this.requestInFlight = true;
    this.lastCheckAt = Date.now();

    try {
      const version = await this.fetchVersion();
      if (!version) {
        return;
      }
      if (!this.currentVersion) {
        this.currentVersion = version;
        return;
      }
      this.detectedServerVersion = version;
      this.tryPresentUpdate();
    } catch {
      /* Wait for the next normal trigger. */
    } finally {
      this.requestInFlight = false;
    }
  }

  private async fetchVersion(): Promise<string | null> {
    try {
      const response = await fetch(`${APP_VERSION_PATH}?t=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });

      if (!response.ok) {
        return null;
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (contentType && !contentType.includes('json') && !contentType.includes('text/plain')) {
        return null;
      }

      const payload: unknown = await response.json();
      return parseAppVersionPayload(payload);
    } catch {
      return null;
    }
  }

  private tryPresentUpdate(): void {
    const version = this.detectedServerVersion;
    if (!version || version === this.currentVersion || version === this.dismissedVersion) {
      this.updatePending = false;
      this.clearPendingListener();
      return;
    }
    if (this.updatePopupOpen) {
      return;
    }
    if (this.hasWorkingDialog()) {
      this.updatePending = true;
      this.listenForDialogsClosed();
      return;
    }
    this.openUpdatePopup(version);
  }

  private hasWorkingDialog(): boolean {
    return this.dialog.openDialogs.some(
      (ref) => ref.id !== APP_VERSION_UPDATE_DIALOG_ID,
    );
  }

  private listenForDialogsClosed(): void {
    if (this.pendingCloseSub) {
      return;
    }
    this.pendingCloseSub = this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
      this.pendingCloseSub = null;
      if (this.updatePopupOpen) {
        return;
      }
      if (this.hasWorkingDialog()) {
        this.listenForDialogsClosed();
        return;
      }
      this.updatePending = false;
      this.tryPresentUpdate();
    });
  }

  private clearPendingListener(): void {
    this.pendingCloseSub?.unsubscribe();
    this.pendingCloseSub = null;
  }

  private openUpdatePopup(version: string): void {
    if (this.updatePopupOpen || this.dialog.getDialogById(APP_VERSION_UPDATE_DIALOG_ID)) {
      return;
    }

    this.updatePending = false;
    this.clearPendingListener();
    this.updatePopupOpen = true;
    this.promptedVersion = version;

    const ref = this.dialog.open<AppVersionUpdateDialog, void, AppVersionUpdateDialogResult>(
      AppVersionUpdateDialog,
      {
        id: APP_VERSION_UPDATE_DIALOG_ID,
        hasBackdrop: false,
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        disableClose: false,
        panelClass: 'app-version-update-panel',
        position: { top: '24px', right: '24px' },
        width: 'min(420px, calc(100vw - 32px))',
      },
    );
    this.updateDialogRef = ref;

    ref.afterClosed().pipe(take(1)).subscribe((result) => {
      this.updatePopupOpen = false;
      this.updateDialogRef = null;

      if (result === 'refresh') {
        this.reloadOnce();
        return;
      }

      this.dismissedVersion = this.promptedVersion;
      this.promptedVersion = null;
      this.updatePending = false;
      this.clearPendingListener();
      this.tryPresentUpdate();
    });
  }

  private reloadOnce(): void {
    if (this.reloadArmed) {
      return;
    }
    this.reloadArmed = true;
    window.location.reload();
  }
}
